import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EditReviewModal from '../../../components/EditReviewModal';
import { toast } from 'react-toastify';

const ReviewsSection = ({ userId }) => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sortOrder, setSortOrder] = useState('recent');
  const [showSortMessage, setShowSortMessage] = useState(false);

  const toggleSort = () => {
    const newOrder = sortOrder === 'recent' ? 'oldest' : 'recent';
    setSortOrder(newOrder);
    setShowSortMessage(true);
    setTimeout(() => setShowSortMessage(false), 1500);
  };

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  // Check if review is within 1-week edit window
  const isWithinEditWindow = (createdAt) => {
    if (!createdAt) return false;
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
    const reviewDate = new Date(createdAt);
    return (Date.now() - reviewDate.getTime()) < oneWeekInMs;
  };

  // Calculate days remaining in edit window
  const getDaysRemaining = (createdAt) => {
    if (!createdAt) return 0;
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
    const reviewDate = new Date(createdAt);
    const msRemaining = oneWeekInMs - (Date.now() - reviewDate.getTime());
    return Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setEditingReview(null);
    fetchReviews();
    toast.success('Review updated successfully!');
  };

  useEffect(() => {
    fetchReviews();
  }, [userId]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/events/feedback/singlefeedback/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setReviews(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalf = (rating || 0) % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<i key={i} className="bi bi-star-fill"></i>);
      } else if (i === fullStars && hasHalf) {
        stars.push(<i key={i} className="bi bi-star-half"></i>);
      } else {
        stars.push(<i key={i} className="bi bi-star"></i>);
      }
    }
    return stars;
  };

  const getRoleLabel = (role) => {
    const roles = {
      attendee: 'Attendee',
      volunteer: 'Volunteer',
      artist: 'Artist',
      invited_guest: 'Invited Guest',
    };
    return roles[role] || 'Attendee';
  };

  if (loading) {
    return (
      <div className="section-content reviews-section">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-content reviews-section">
      <div className="section-header">
        <h1>My Reviews</h1>
        <p className="section-subtitle">Reviews you've written for events you attended</p>
      </div>

      {reviews.length === 0 ? (
        <div className="empty-state">
          <i className="bi bi-chat-square-quote"></i>
          <h3>No reviews yet</h3>
          <p>After attending an event, share your experience to help the community!</p>
          <button className="primary-btn" onClick={() => navigate('/events')}>
            Find Events
          </button>
        </div>
      ) : (
        <div className="reviews-list">
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
            {showSortMessage && (
              <span style={{
                fontSize: '0.8rem',
                color: '#6B7280',
                animation: 'fadeIn 0.2s ease-out'
              }}>
                {sortOrder === 'recent' ? 'Most recent' : 'Oldest first'}
              </span>
            )}
            <button
              onClick={toggleSort}
              title={sortOrder === 'recent' ? 'Showing most recent' : 'Showing oldest first'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#9CA3AF',
                display: 'flex',
                alignItems: 'center',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
            >
              <i className={`bi ${sortOrder === 'recent' ? 'bi-sort-down' : 'bi-sort-up'}`} style={{ fontSize: '1.1rem' }}></i>
            </button>
          </div>
          {[...reviews].sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
          }).map((review) => (
            <div key={review._id} className="review-card">
              <div className="review-event-info">
                {review.eventId?.eventImage && (
                  <img
                    src={review.eventId.eventImage}
                    alt={review.eventId.title}
                    className="event-thumbnail"
                    onClick={() => review.eventId?._id && navigate(`/event/${review.eventId._id}`)}
                  />
                )}
                <div className="event-details">
                  <h4 onClick={() => review.eventId?._id && navigate(`/event/${review.eventId._id}`)}>
                    {review.eventId?.title || 'Event'}
                  </h4>
                </div>
                <div className="event-meta-right">
                  <span className="event-date">
                    <i className="bi bi-calendar3"></i>
                    {formatDate(review.eventId?.startDateTime)}
                  </span>
                  {review.eventId?.location && (
                    <span className="event-location">
                      <i className="bi bi-geo-alt"></i>
                      {review.eventId.location}
                    </span>
                  )}
                </div>
              </div>

              <div className="review-content">
                <div className="review-header">
                  <div className="review-rating">
                    <div className="stars">{renderStars(review.stars)}</div>
                    <span className="rating-value">{(review.stars || 0).toFixed(1)}</span>
                  </div>
                  <div className="review-meta">
                    <span className="role-badge">{getRoleLabel(review.role)}</span>
                    <span className="review-date">{formatDate(review.createdAt)}</span>
                  </div>
                </div>

                {review.title && <h5 className="review-title">{review.title}</h5>}

                <p className="review-text">{review.description}</p>

                {/* Rating Categories */}
                <div className="rating-categories">
                  {review.venue > 0 && (
                    <span className="category-badge">
                      <i className="bi bi-building"></i> Venue: {review.venue}
                    </span>
                  )}
                  {review.ratio > 0 && (
                    <span className="category-badge">
                      <i className="bi bi-yin-yang"></i> Ratio: {review.ratio}
                    </span>
                  )}
                  {review.organization > 0 && (
                    <span className="category-badge">
                      <i className="bi bi-clipboard-check"></i> Org: {review.organization}
                    </span>
                  )}
                  {review.artists > 0 && (
                    <span className="category-badge">
                      <i className="bi bi-music-note"></i> Artists: {review.artists}
                    </span>
                  )}
                  {review.culture > 0 && (
                    <span className="category-badge">
                      <i className="bi bi-globe"></i> Culture: {review.culture}
                    </span>
                  )}
                  {review.safety > 0 && (
                    <span className="category-badge">
                      <i className="bi bi-shield-check"></i> Safety: {review.safety}
                    </span>
                  )}
                </div>

                <div className="review-actions-row">
                  <div className="review-stats">
                    <span className="like-count">
                      <i className="bi bi-heart"></i> {review.likeCount || 0} likes
                    </span>
                  </div>
                  {isWithinEditWindow(review.createdAt) && (
                    <button
                      className="edit-review-btn"
                      onClick={() => handleEditClick(review)}
                      title={`Edit available for ${getDaysRemaining(review.createdAt)} more day(s)`}
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Review Modal */}
      {showEditModal && editingReview && (
        <EditReviewModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingReview(null);
          }}
          review={editingReview}
          onSuccess={handleEditSuccess}
          apiBase={API_BASE}
        />
      )}
    </div>
  );
};

export default ReviewsSection;
